/**
 * ALTIORA · LiveSignalsSection — Preuve que la plateforme est vivante.
 *
 * Combine deux éléments côte à côte :
 *  1. Signaux macro-économiques réels (Statistique Canada · WDS)
 *  2. Feed d'opportunités type (nouvelles inc., événements, appels d'offres)
 *
 * Objectif : montrer au visiteur qu'ALTIORA est branchée en continu
 * sur des sources de données réelles, pas un pitch décoratif.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Activity, Building2, CalendarClock, FileText, Sparkles,
  TrendingUp, TrendingDown, Minus,
} from "lucide-react";
import { api } from "@/lib/api";

const NAVY = "#0B2545";
const NAVY_DEEP = "#061528";
const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const SLATE = "#64748B";
const GREEN = "#0B7A3B";
const RED = "#B91C1C";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 0.68, 0.24, 1] },
};

// ─── Feed statique de démonstration (opportunités type) ──────────────
// Reflète ce qu'ALTIORA détecte en continu dans la vraie plateforme.
const DEMO_FEED = [
  { icon: Building2,     label: "Nouvelle immatriculation · Groupe Nadeau Fiscalité",
    meta: "REQ · Lévis · 12 min", type: "Entreprise",       color: GOLD },
  { icon: CalendarClock, label: "Événement · Sommet CFA Québec — networking",
    meta: "CFA · 18 févr. · 14h", type: "Réseautage",       color: CHAMPAGNE },
  { icon: FileText,      label: "Appel d'offres · Ville de Sherbrooke — conseil stratégique",
    meta: "SEAO · publié aujourd'hui", type: "Marché public", color: GOLD },
  { icon: Sparkles,      label: "Signal · Levée de fonds série B — Cabinet Fintech local",
    meta: "Newswire · il y a 2 h",     type: "Croissance",   color: CHAMPAGNE },
  { icon: Building2,     label: "Nouvelle immatriculation · Beaudry Investissements",
    meta: "REQ · Québec · 42 min",     type: "Entreprise",   color: GOLD },
];


function SignalIcon({ signal }) {
  if (signal === "positive") return <TrendingUp size={13} strokeWidth={2} style={{ color: GREEN }} />;
  if (signal === "negative") return <TrendingDown size={13} strokeWidth={2} style={{ color: RED }} />;
  return <Minus size={13} strokeWidth={2} style={{ color: SLATE }} />;
}

function fmtValue(v, unit) {
  if (v === null || v === undefined) return "—";
  const num = Number(v).toLocaleString("fr-CA", { maximumFractionDigits: 1 });
  return `${num}${unit === "%" ? " %" : unit === "k" ? " k" : ""}`;
}
function fmtDelta(d, unit) {
  if (d === null || d === undefined) return "";
  const sign = d > 0 ? "+" : "";
  const num = Number(d).toLocaleString("fr-CA", { maximumFractionDigits: 2 });
  return `${sign}${num}${unit === "%" ? " pt" : unit === "k" ? " k" : ""}`;
}


export default function LiveSignalsSection() {
  const [macro, setMacro] = useState(null);
  const [region, setRegion] = useState("canada");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/territory/signals");
        if (!cancelled) setMacro(data);
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeRegion = macro?.regions?.[region] || macro?.regions?.canada;
  const indicators = activeRegion?.indicators || {};

  return (
    <section
      className="border-t"
      style={{ borderColor: HAIRLINE, background: IVORY }}
      data-testid="section-live-signals"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        {/* En-tête section */}
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <motion.div {...fadeUp}>
            <div className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.32em]"
                 style={{ color: CHAMPAGNE }}>
              <span className="h-px w-6" style={{ background: CHAMPAGNE }} />
              Signaux en direct
            </div>
            <div className="mt-6 font-serif leading-[1.1] tracking-tight max-w-3xl"
                 style={{ color: INK, fontSize: "clamp(1.9rem, 4vw, 2.8rem)", fontWeight: 400 }}>
              La plateforme est
              <span style={{ fontStyle: "italic", color: GOLD }}> alimentée en continu</span>.
            </div>
          </motion.div>
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest"
            style={{ color: SLATE }}
          >
            <span className="relative inline-flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60"
                    style={{ background: GREEN }} />
              <span className="relative inline-flex rounded-full h-2 w-2" style={{ background: GREEN }} />
            </span>
            <span>Sources publiques · rafraîchies en continu</span>
          </motion.div>
        </div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.15 }}
          className="mt-6 max-w-2xl text-[15px] md:text-[16px] leading-[1.7]"
          style={{ color: INK_SOFT }}
        >
          ALTIORA agrège en continu registres publics, appels d'offres,
          événements de réseautage et signaux macro-économiques —
          transformant le bruit en opportunités qualifiées.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-6">

          {/* Colonne gauche · Macro Statistique Canada ─────────────── */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="lg:col-span-2 rounded-xl p-6"
            style={{ background: "#FFFFFF", border: `1px solid ${HAIRLINE}` }}
            data-testid="live-signals-macro"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Activity size={14} strokeWidth={1.8} style={{ color: NAVY }} />
                <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: NAVY }}>
                  Pouls économique
                </div>
              </div>
              {/* Region switcher */}
              {macro?.regions && (
                <div className="inline-flex rounded-md overflow-hidden flex-wrap"
                     style={{ border: `1px solid ${CHAMPAGNE}44` }}>
                  {["canada", "quebec", "ontario", "manitoba", "alberta", "british_columbia"].map((k) => {
                    if (!macro.regions[k]) return null;
                    const on = k === region;
                    const short = {
                      canada: "CA", quebec: "QC", ontario: "ON",
                      manitoba: "MB", alberta: "AB", british_columbia: "C.-B.",
                    }[k] || k;
                    return (
                      <button
                        key={k}
                        onClick={() => setRegion(k)}
                        data-testid={`live-signals-region-${k}`}
                        aria-pressed={on}
                        className="px-2 py-1 text-[9px] uppercase tracking-widest font-mono transition-colors"
                        style={{
                          background: on ? NAVY : "transparent",
                          color: on ? IVORY : NAVY,
                          fontWeight: on ? 700 : 500,
                        }}
                      >
                        {short}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {loading ? (
              <div className="mt-6 text-[12px]" style={{ color: SLATE }}>
                Chargement…
              </div>
            ) : Object.keys(indicators).length === 0 ? (
              <div className="mt-6 text-[12px]" style={{ color: SLATE }}>
                Indicateurs macro indisponibles pour le moment.
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {Object.entries(indicators).map(([key, ind]) => {
                  const positive = ind.signal === "positive";
                  const negative = ind.signal === "negative";
                  const accent = positive ? GREEN : negative ? RED : SLATE;
                  return (
                    <div key={key} className="border-b pb-3"
                         style={{ borderColor: HAIRLINE }}
                         data-testid={`live-signals-indicator-${key}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-[10px] font-mono uppercase tracking-[0.22em]"
                             style={{ color: CHAMPAGNE }}>
                          {ind.label_fr}
                        </div>
                        <div className="flex items-center gap-1 text-[11px]" style={{ color: accent }}>
                          <SignalIcon signal={ind.signal} />
                          <span className="font-mono">{fmtDelta(ind.delta, ind.unit)}</span>
                        </div>
                      </div>
                      <div className="font-serif text-2xl tracking-tight mt-1" style={{ color: INK }}>
                        {fmtValue(ind.latest, ind.unit)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {macro?.source && (
              <div className="mt-4 text-[10px] font-mono uppercase tracking-widest" style={{ color: SLATE }}>
                Source · {macro.source}
              </div>
            )}
          </motion.div>

          {/* Colonne droite · Feed opportunités ─────────────────────── */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.28 }}
            className="lg:col-span-3 rounded-xl p-6"
            style={{ background: "#FFFFFF", border: `1px solid ${HAIRLINE}` }}
            data-testid="live-signals-feed"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles size={14} strokeWidth={1.8} style={{ color: NAVY }} />
                <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: NAVY }}>
                  Opportunités détectées · exemples
                </div>
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: GOLD }}>
                Aperçu démo
              </div>
            </div>

            <div className="mt-6 space-y-3">
              {DEMO_FEED.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, x: -8 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
                  className="flex items-center gap-3 py-3"
                  style={{ borderBottom: i < DEMO_FEED.length - 1 ? `1px solid ${HAIRLINE}` : "none" }}
                  data-testid={`live-signals-feed-${i}`}
                >
                  <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
                       style={{ background: `${row.color}18`, color: row.color }}>
                    <row.icon size={15} strokeWidth={1.7} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[13.5px] leading-tight" style={{ color: INK }}>
                      {row.label}
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest mt-1"
                         style={{ color: SLATE }}>
                      {row.meta}
                    </div>
                  </div>
                  <span className="px-2 py-0.5 rounded text-[9px] font-mono uppercase tracking-widest flex-shrink-0"
                        style={{ background: `${row.color}18`, color: row.color }}>
                    {row.type}
                  </span>
                </motion.div>
              ))}
            </div>

            <div className="mt-6 flex items-center justify-between text-[11px]" style={{ color: SLATE }}>
              <span>Sources agrégées · REQ · SEAO · CFA · Newswire · LinkedIn</span>
              <span className="font-mono uppercase tracking-widest" style={{ color: GOLD }}>
                + de 40 sources actives
              </span>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
